import React, { useState, useMemo } from 'react'
import { DcFlexBox, DcDiv, DcText, DcDateRangePicker, colors } from 'pixel-base'
import PixelTable from 'pixel-base/components/pixel-table/pixel-table'
import {
  TopProduct,
  sortProductsData,
  downloadProductsCSV,
} from './topProductsUtils'

interface TopProductsDetailPaneProps {
  data: TopProduct[]
  loading: boolean
  onClose: () => void
  onFiltersChange?: (filters: {
    start_date: Date
    end_date: Date
    data_type: string
  }) => void
  dashboardFilters?: {
    start_date: Date
    end_date: Date
    data_type: string
  }
}

const TopProductsDetailPane: React.FC<TopProductsDetailPaneProps> = ({
  data,
  loading,
  onClose: _onClose,
  onFiltersChange,
  dashboardFilters,
}) => {
  const [sortBy, setSortBy] = useState<'revenue' | 'items_sold'>('revenue')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [tableSortBy, setTableSortBy] = useState<{
    field: string
    direction: 'asc' | 'desc'
  } | null>(null)

  // Date filter state for detail pane - initialize with dashboard filters
  const [detailFilters, setDetailFilters] = useState({
    start_date:
      dashboardFilters?.start_date ||
      new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end_date: dashboardFilters?.end_date || new Date(),
    data_type: dashboardFilters?.data_type || 'daily',
  })

  // Reset detail filters when dashboard filters change
  React.useEffect(() => {
    if (dashboardFilters) {
      setDetailFilters(dashboardFilters)
    }
  }, [dashboardFilters])

  // Handle row expansion
  const handleRowExpand = (row: TopProduct) => {
    const newExpanded = new Set(expandedRows)
    const productId = String(row.base_product_id)
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId)
    } else {
      newExpanded.add(productId)
    }
    setExpandedRows(newExpanded)
  }

  // Handle table column sorting
  const handleTableSort = (field: string) => {
    if (tableSortBy?.field === field) {
      // Toggle direction if same field
      setTableSortBy({
        field,
        direction: tableSortBy.direction === 'asc' ? 'desc' : 'asc',
      })
    } else {
      // Set new field with default ascending direction
      setTableSortBy({ field, direction: 'asc' })
    }
  }

  // Sort data based on current sort criteria
  const sortedData = useMemo(() => {
    let sorted = sortProductsData(data, sortBy)

    // Apply table sorting if specified
    if (tableSortBy) {
      sorted = [...sorted].sort((a, b) => {
        let aValue: number
        let bValue: number

        switch (tableSortBy.field) {
          case 'revenue':
            aValue = a.total_revenue
            bValue = b.total_revenue
            break
          case 'items_sold':
            aValue = a.total_items_sold
            bValue = b.total_items_sold
            break
          case 'items_returned':
            aValue = a.items_returned || 0
            bValue = b.items_returned || 0
            break
          case 'return_rate': {
            const aTotalItems = a.total_items_sold + (a.items_returned || 0)
            const bTotalItems = b.total_items_sold + (b.items_returned || 0)
            aValue =
              aTotalItems > 0
                ? ((a.items_returned || 0) / aTotalItems) * 100
                : 0
            bValue =
              bTotalItems > 0
                ? ((b.items_returned || 0) / bTotalItems) * 100
                : 0
            break
          }
          case 'avg_price':
            aValue =
              a.total_items_sold > 0 ? a.total_revenue / a.total_items_sold : 0
            bValue =
              b.total_items_sold > 0 ? b.total_revenue / b.total_items_sold : 0
            break
          default:
            return 0
        }

        if (tableSortBy.direction === 'asc') {
          return aValue - bValue
        }
        return bValue - aValue
      })
    }

    return sorted
  }, [data, sortBy, tableSortBy])

  // CSV download functionality using shared utility
  const downloadCSV = () => downloadProductsCSV(sortedData, sortBy)

  // Expanded row component for PixelTable
  const ExpandedRowComponent = ({ data: product }: { data: TopProduct }) => {
    const totalItems = product.total_items_sold || 0

    return (
      <DcDiv
        style={{
          padding: '16px 20px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
        }}
      >
        {/* Progress Bar Section */}

        {/* Variation Details Grid */}
        <DcDiv style={{ display: 'grid', gap: '8px' }}>
          {product.variations?.map((variation, index) => {
            const percentage =
              totalItems > 0 ? (variation.items_sold / totalItems) * 100 : 0
            const variationTotalItems =
              variation.items_sold + (variation.items_returned || 0)
            const returnPercentage =
              variationTotalItems > 0
                ? ((variation.items_returned || 0) / variationTotalItems) * 100
                : 0

            const netRevenue =
              variation.revenue - (variation.revenue_returned || 0)
            const netItems =
              variation.items_sold - (variation.items_returned || 0)
            const hasReturns = (variation.items_returned || 0) > 0

            return (
              <DcDiv
                key={variation.variation_id}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef',
                  borderLeft: `3px solid hsl(${
                    200 + ((index * 40) % 360)
                  }, 70%, 60%)`,
                }}
              >
                {/* Header Row */}
                <DcFlexBox
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ marginBottom: '6px' }}
                >
                  <DcDiv>
                    <DcText
                      style={{
                        fontWeight: '600',
                        fontSize: '11px',
                        marginBottom: '2px',
                      }}
                    >
                      {variation.product_name}
                    </DcText>
                    <DcText
                      style={{
                        fontSize: '9px',
                        color: '#6c757d',
                      }}
                    >
                      SKU: {variation.sku}
                    </DcText>
                  </DcDiv>

                  {/* Middle Section - Total Numbers */}
                  <DcDiv
                    style={{
                      display: 'flex',
                      gap: '16px',
                      alignItems: 'center',
                    }}
                  >
                    <DcDiv style={{ textAlign: 'center' }}>
                      <DcText
                        style={{
                          fontSize: '8px',
                          color: '#6c757d',
                          marginBottom: '1px',
                        }}
                      >
                        Total Sold
                      </DcText>
                      <DcText
                        style={{
                          fontWeight: '600',
                          color: '#495057',
                          fontSize: '10px',
                        }}
                      >
                        {variation.items_sold.toLocaleString()}
                      </DcText>
                    </DcDiv>
                    <DcDiv style={{ textAlign: 'center' }}>
                      <DcText
                        style={{
                          fontSize: '8px',
                          color: '#6c757d',
                          marginBottom: '1px',
                        }}
                      >
                        Revenue
                      </DcText>
                      <DcText
                        style={{
                          fontWeight: '600',
                          color: '#28a745',
                          fontSize: '10px',
                        }}
                      >
                        PKR {variation.revenue.toLocaleString()}
                      </DcText>
                    </DcDiv>
                    {hasReturns && (
                      <>
                        <DcDiv style={{ textAlign: 'center' }}>
                          <DcText
                            style={{
                              fontSize: '8px',
                              color: '#6c757d',
                              marginBottom: '1px',
                            }}
                          >
                            Returned
                          </DcText>
                          <DcText
                            style={{
                              fontWeight: '600',
                              color: '#dc3545',
                              fontSize: '10px',
                            }}
                          >
                            {(variation.items_returned || 0).toLocaleString()}
                          </DcText>
                        </DcDiv>
                        <DcDiv style={{ textAlign: 'center' }}>
                          <DcText
                            style={{
                              fontSize: '8px',
                              color: '#6c757d',
                              marginBottom: '1px',
                            }}
                          >
                            Rev Returned
                          </DcText>
                          <DcText
                            style={{
                              fontWeight: '600',
                              color: '#dc3545',
                              fontSize: '10px',
                            }}
                          >
                            PKR{' '}
                            {(variation.revenue_returned || 0).toLocaleString()}
                          </DcText>
                        </DcDiv>
                      </>
                    )}
                  </DcDiv>

                  <DcDiv
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <DcDiv
                      style={{
                        width: '40px',
                        height: '4px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '2px',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <DcDiv
                        style={{
                          width: `${percentage}%`,
                          height: '100%',
                          background: `hsl(${
                            200 + ((index * 40) % 360)
                          }, 70%, 60%)`,
                        }}
                      />
                      {hasReturns && (
                        <DcDiv
                          style={{
                            width: `${returnPercentage}%`,
                            height: '100%',
                            background: '#dc3545',
                            opacity: 0.7,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                          }}
                        />
                      )}
                    </DcDiv>
                    <DcText
                      style={{
                        fontSize: '9px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      {percentage.toFixed(1)}%
                    </DcText>
                    {hasReturns && (
                      <DcText
                        style={{
                          fontSize: '9px',
                          color: '#dc3545',
                          fontWeight: '500',
                        }}
                      >
                        ({returnPercentage.toFixed(1)}%)
                      </DcText>
                    )}
                  </DcDiv>
                </DcFlexBox>

                {/* Metrics Row */}
                <DcFlexBox
                  justifyContent="space-between"
                  alignItems="center"
                  gap="16px"
                >
                  <DcDiv
                    style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center',
                    }}
                  >
                    <DcDiv style={{ textAlign: 'center', minWidth: '60px' }}>
                      <DcText
                        style={{
                          fontSize: '8px',
                          color: '#6c757d',
                          marginBottom: '1px',
                        }}
                      >
                        Revenue
                      </DcText>
                      <DcText
                        style={{
                          fontWeight: '600',
                          color: '#28a745',
                          fontSize: '10px',
                        }}
                      >
                        PKR {variation.revenue.toLocaleString()}
                      </DcText>
                    </DcDiv>
                    <DcDiv style={{ textAlign: 'center', minWidth: '50px' }}>
                      <DcText
                        style={{
                          fontSize: '8px',
                          color: '#6c757d',
                          marginBottom: '1px',
                        }}
                      >
                        Sold
                      </DcText>
                      <DcText
                        style={{
                          fontWeight: '600',
                          color: '#495057',
                          fontSize: '10px',
                        }}
                      >
                        {variation.items_sold.toLocaleString()}
                      </DcText>
                    </DcDiv>
                  </DcDiv>

                  {hasReturns && (
                    <DcDiv
                      style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center',
                      }}
                    >
                      <DcDiv style={{ textAlign: 'center', minWidth: '50px' }}>
                        <DcText
                          style={{
                            fontSize: '8px',
                            color: '#6c757d',
                            marginBottom: '1px',
                          }}
                        >
                          Returned
                        </DcText>
                        <DcText
                          style={{
                            fontWeight: '600',
                            color: '#dc3545',
                            fontSize: '10px',
                          }}
                        >
                          {(variation.items_returned || 0).toLocaleString()}
                        </DcText>
                      </DcDiv>
                      <DcDiv style={{ textAlign: 'center', minWidth: '60px' }}>
                        <DcText
                          style={{
                            fontSize: '8px',
                            color: '#6c757d',
                            marginBottom: '1px',
                          }}
                        >
                          Net Revenue
                        </DcText>
                        <DcText
                          style={{
                            fontWeight: '600',
                            color: '#28a745',
                            fontSize: '10px',
                          }}
                        >
                          PKR {netRevenue.toLocaleString()}
                        </DcText>
                      </DcDiv>
                      <DcDiv style={{ textAlign: 'center', minWidth: '50px' }}>
                        <DcText
                          style={{
                            fontSize: '8px',
                            color: '#6c757d',
                            marginBottom: '1px',
                          }}
                        >
                          Net Items
                        </DcText>
                        <DcText
                          style={{
                            fontWeight: '600',
                            color: '#28a745',
                            fontSize: '10px',
                          }}
                        >
                          {netItems.toLocaleString()}
                        </DcText>
                      </DcDiv>
                    </DcDiv>
                  )}
                </DcFlexBox>
              </DcDiv>
            )
          })}
        </DcDiv>
      </DcDiv>
    )
  }

  // Table columns configuration for PixelTable
  const tableColumns = [
    {
      name: 'Product',
      selector: (row: TopProduct) => row.product_name,
      width: '25%',
      cell: (row: TopProduct) => (
        <DcFlexBox alignItems="center" gap="12px">
          <DcDiv
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '8px',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {row.variations?.[0]?.image ? (
              <img
                src={row.variations[0].image}
                alt={row.product_name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <DcDiv
                style={{
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DcText style={{ fontSize: '10px', color: '#6c757d' }}>
                  No Image
                </DcText>
              </DcDiv>
            )}
          </DcDiv>
          <DcDiv style={{ flex: 1, minWidth: 0 }}>
            <DcText
              style={{
                fontWeight: '600',
                color: '#212529',
                marginBottom: '4px',
              }}
            >
              {row.product_name}
            </DcText>
            <DcText style={{ fontSize: '11px', color: '#6c757d' }}>
              SKU: {row.variations?.[0]?.sku || 'N/A'}
            </DcText>
          </DcDiv>
        </DcFlexBox>
      ),
    },
    {
      name: (
        <DcDiv
          onClick={() => handleTableSort('revenue')}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            userSelect: 'none',
          }}
        >
          Revenue (PKR)
          {tableSortBy?.field === 'revenue' && (
            <DcText style={{ fontSize: '10px', color: '#6c757d' }}>
              {tableSortBy.direction === 'asc' ? '↑' : '↓'}
            </DcText>
          )}
        </DcDiv>
      ),
      selector: (row: TopProduct) => row.total_revenue,
      width: '15%',
      right: true,
      sortable: true,
      cell: (row: TopProduct) => (
        <DcText style={{ fontWeight: '600', color: '#28a745' }}>
          PKR {row.total_revenue.toLocaleString()}
        </DcText>
      ),
    },
    {
      name: (
        <DcDiv
          onClick={() => handleTableSort('items_sold')}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            userSelect: 'none',
          }}
        >
          Items Sold
          {tableSortBy?.field === 'items_sold' && (
            <DcText style={{ fontSize: '10px', color: '#6c757d' }}>
              {tableSortBy.direction === 'asc' ? '↑' : '↓'}
            </DcText>
          )}
        </DcDiv>
      ),
      selector: (row: TopProduct) => row.total_items_sold,
      width: '10%',
      right: true,
      sortable: true,
      cell: (row: TopProduct) => (
        <DcText style={{ fontWeight: '500', color: '#495057' }}>
          {row.total_items_sold.toLocaleString()}
        </DcText>
      ),
    },
    {
      name: (
        <DcDiv
          onClick={() => handleTableSort('items_returned')}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            userSelect: 'none',
          }}
        >
          Items Returned
          {tableSortBy?.field === 'items_returned' && (
            <DcText style={{ fontSize: '10px', color: '#6c757d' }}>
              {tableSortBy.direction === 'asc' ? '↑' : '↓'}
            </DcText>
          )}
        </DcDiv>
      ),
      selector: (row: TopProduct) => row.items_returned || 0,
      width: '10%',
      right: true,
      sortable: true,
      cell: (row: TopProduct) => (
        <DcText style={{ fontWeight: '500', color: '#dc3545' }}>
          {(row.items_returned || 0).toLocaleString()}
        </DcText>
      ),
    },
    {
      name: (
        <DcDiv
          onClick={() => handleTableSort('return_rate')}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            userSelect: 'none',
          }}
        >
          Return Rate
          {tableSortBy?.field === 'return_rate' && (
            <DcText style={{ fontSize: '10px', color: '#6c757d' }}>
              {tableSortBy.direction === 'asc' ? '↑' : '↓'}
            </DcText>
          )}
        </DcDiv>
      ),
      selector: (row: TopProduct) => {
        const totalItems = row.total_items_sold + (row.items_returned || 0)
        return totalItems > 0
          ? ((row.items_returned || 0) / totalItems) * 100
          : 0
      },
      width: '8%',
      right: true,
      sortable: true,
      cell: (row: TopProduct) => {
        const totalItems = row.total_items_sold + (row.items_returned || 0)
        const returnRate =
          totalItems > 0
            ? (((row.items_returned || 0) / totalItems) * 100).toFixed(1)
            : '0.0'
        return (
          <DcText style={{ fontWeight: '500', color: '#dc3545' }}>
            {returnRate}%
          </DcText>
        )
      },
    },
    {
      name: (
        <DcDiv
          onClick={() => handleTableSort('avg_price')}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            userSelect: 'none',
          }}
        >
          Avg Price
          {tableSortBy?.field === 'avg_price' && (
            <DcText style={{ fontSize: '10px', color: '#6c757d' }}>
              {tableSortBy.direction === 'asc' ? '↑' : '↓'}
            </DcText>
          )}
        </DcDiv>
      ),
      selector: (row: TopProduct) => row.total_revenue / row.total_items_sold,
      width: '12%',
      right: true,
      sortable: true,
      cell: (row: TopProduct) => {
        const avgPrice =
          row.total_items_sold > 0
            ? (row.total_revenue / row.total_items_sold).toFixed(2)
            : '0.00'
        return (
          <DcText style={{ fontWeight: '500', color: '#6c757d' }}>
            PKR {avgPrice}
          </DcText>
        )
      },
    },
    {
      name: 'Collections',
      selector: (row: TopProduct) =>
        row.collection_names?.join(', ') || 'No collections',
      width: '18%',
      cell: (row: TopProduct) => (
        <DcDiv>
          {row.collection_names?.length > 0 ? (
            <DcFlexBox flexWrap="wrap" gap="4px">
              {row.collection_names.slice(0, 2).map((collection, idx) => (
                <span
                  key={idx}
                  style={{
                    background:
                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '12px',
                    fontSize: '10px',
                    fontWeight: '500',
                  }}
                >
                  {collection}
                </span>
              ))}
              {row.collection_names.length > 2 && (
                <span
                  style={{
                    fontSize: '10px',
                    color: '#6c757d',
                    alignSelf: 'center',
                  }}
                >
                  +{row.collection_names.length - 2} more
                </span>
              )}
            </DcFlexBox>
          ) : (
            <span style={{ color: '#6c757d', fontSize: '11px' }}>
              No collections
            </span>
          )}
        </DcDiv>
      ),
    },
    {
      name: 'Variations',
      selector: (row: TopProduct) => row.variations?.length || 0,
      width: '12%',
      center: true,
      cell: (row: TopProduct) => (
        <DcDiv
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <span
            style={{
              background: row.variations?.length > 0 ? '#28a745' : '#6c757d',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '500',
            }}
          >
            {row.variations?.length || 0}
          </span>
        </DcDiv>
      ),
    },
  ]

  return (
    <DcDiv style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Panel Header with Controls */}
      <DcDiv
        style={{
          padding: '16px 24px',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
          backgroundColor: 'white',
        }}
      >
        {/* Panel Level Controls */}
        <DcFlexBox
          gap="24px"
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Revenue/Items Toggle - Same as Chart */}
          <DcDiv>
            <DcText
              style={{
                fontSize: '12px',
                color: '#6c757d',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Sort By
            </DcText>
            <DcFlexBox
              style={{
                border: `2px solid ${colors.grayBorderLight}`,
                borderRadius: '8px',
                overflow: 'hidden',
                background: 'white',
              }}
            >
              <button
                onClick={() => setSortBy('revenue')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background:
                    sortBy === 'revenue' ? colors.primary : 'transparent',
                  color: sortBy === 'revenue' ? 'white' : colors.text,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  minWidth: '80px',
                }}
              >
                Revenue
              </button>
              <button
                onClick={() => setSortBy('items_sold')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background:
                    sortBy === 'items_sold' ? colors.primary : 'transparent',
                  color: sortBy === 'items_sold' ? 'white' : colors.text,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  minWidth: '80px',
                }}
              >
                Items Sold
              </button>
            </DcFlexBox>
          </DcDiv>

          {/* Download CSV Button */}
          <DcDiv>
            <DcText
              style={{
                fontSize: '12px',
                color: '#6c757d',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Export
            </DcText>
            <button
              onClick={downloadCSV}
              disabled={!sortedData.length}
              style={{
                padding: '10px 20px',
                border: `2px solid ${colors.primary}`,
                background: 'transparent',
                color: colors.primary,
                cursor: sortedData.length ? 'pointer' : 'not-allowed',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                opacity: sortedData.length ? 1 : 0.5,
                minWidth: '120px',
              }}
              onMouseEnter={(e) => {
                if (sortedData.length) {
                  e.currentTarget.style.background = colors.primary
                  e.currentTarget.style.color = 'white'
                }
              }}
              onMouseLeave={(e) => {
                if (sortedData.length) {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = colors.primary
                }
              }}
            >
              Download CSV
            </button>
          </DcDiv>

          {/* Date Filter */}
          <DcDiv>
            <DcText
              style={{
                fontSize: '12px',
                color: '#6c757d',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Date Range
            </DcText>
            <DcDateRangePicker
              size="sm"
              month={2}
              ranges={{
                start_date: detailFilters.start_date,
                end_date: detailFilters.end_date,
              }}
              onApply={() => {
                if (onFiltersChange) {
                  onFiltersChange(detailFilters)
                }
              }}
              onChange={(e) => {
                const newFilters = {
                  start_date: e.range1.startDate,
                  end_date: e.range1.endDate,
                  data_type: e.range1.data_type || 'daily',
                }
                setDetailFilters(newFilters)
                if (onFiltersChange) {
                  onFiltersChange(newFilters)
                }
              }}
            />
          </DcDiv>
        </DcFlexBox>
      </DcDiv>

      {/* Content Area */}
      <DcDiv style={{ flex: 1, padding: '24px 32px', overflow: 'auto' }}>
        {loading ? (
          <DcFlexBox justifyContent="center" alignItems="center" height="100%">
            <DcText>Loading...</DcText>
          </DcFlexBox>
        ) : (
          <DcDiv style={{ height: '100%', overflow: 'auto', width: '100%' }}>
            <PixelTable
              key={`table-${sortedData.length}`}
              columns={tableColumns}
              data={sortedData}
              pagination={false}
              expandableRows={true}
              expandableRowsComponent={ExpandedRowComponent}
              expandableRowExpanded={(row: TopProduct) =>
                expandedRows.has(String(row.base_product_id))
              }
              onRowClicked={handleRowExpand}
              customStyles={{
                table: {
                  style: {
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'auto',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minWidth: '100%',
                  },
                },
                tableWrapper: {
                  style: {
                    overflow: 'auto',
                    width: '100%',
                  },
                },
              }}
            />
          </DcDiv>
        )}
      </DcDiv>
    </DcDiv>
  )
}

export default TopProductsDetailPane
